import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ImageLightbox = ({ 
  isOpen, 
  onClose, 
  project, 
  initialImageIndex = 0,
  onBookService 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    setCurrentImageIndex(initialImageIndex);
  }, [initialImageIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e?.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'ArrowRight':
          handleNextImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handlePrevImage = () => {
    if (!project?.images?.length) return;
    setCurrentImageIndex(prev => 
      prev === 0 ? project?.images?.length - 1 : prev - 1
    );
    setIsZoomed(false);
  };

  const handleNextImage = () => {
    if (!project?.images?.length) return;
    setCurrentImageIndex(prev => 
      prev === project?.images?.length - 1 ? 0 : prev + 1
    );
    setIsZoomed(false);
  };

  const handleImageClick = () => {
    setIsZoomed(!isZoomed);
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: project?.title,
        text: project?.description,
        url: window.location?.href
      });
    } else {
      navigator.clipboard?.writeText(window.location?.href);
    }
  };

  const handleBookService = () => {
    onBookService(project?.serviceType);
    onClose();
  };

  if (!isOpen || !project) return null;

  const currentImage = project?.images?.[currentImageIndex];

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/50 to-transparent p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <Icon name="X" size={24} />
            </Button>
            <div className="text-white">
              <h3 className="font-semibold">{project?.title}</h3>
              <p className="text-sm text-white/80">
                {currentImageIndex + 1} of {project?.images?.length}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="text-white hover:bg-white/20"
            >
              <Icon name="Share2" size={20} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleImageClick}
              className="text-white hover:bg-white/20"
            >
              <Icon name={isZoomed ? "ZoomOut" : "ZoomIn"} size={20} />
            </Button>
          </div>
        </div>
      </div>
      {/* Main Image */}
      <div className="relative w-full h-full flex items-center justify-center p-4 pt-20 pb-32">
        <div 
          className={`relative max-w-full max-h-full transition-transform duration-300 cursor-pointer ${
            isZoomed ? 'scale-150' : 'scale-100'
          }`}
          onClick={handleImageClick}
        >
          <Image
            src={currentImage?.url}
            alt={currentImage?.alt || project?.title}
            className="max-w-full max-h-full object-contain rounded-lg shadow-elevation-5"
          />
          
          {/* Before/After Labels */}
          {currentImage?.type === 'before' && (
            <div className="absolute top-2 left-2 bg-error text-white px-3 py-1 rounded text-sm font-medium">
              Before
            </div>
          )}
          {currentImage?.type === 'after' && (
            <div className="absolute top-2 left-2 bg-success text-white px-3 py-1 rounded text-sm font-medium">
              After
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        {project?.images?.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <Icon name="ChevronLeft" size={24} />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
            >
              <Icon name="ChevronRight" size={24} />
            </button>
          </>
        )}
      </div>
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
        <div className="max-w-4xl mx-auto">
          {/* Image Description */}
          {currentImage?.description && (
            <p className="text-white/90 text-center mb-4">
              {currentImage?.description}
            </p>
          )}

          {/* Thumbnail Navigation */}
          {project?.images?.length > 1 && (
            <div className="flex justify-center space-x-2 mb-4 overflow-x-auto pb-2">
              {project?.images?.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors duration-200 ${
                    index === currentImageIndex 
                      ? 'border-primary' :'border-white/30 hover:border-white/60'
                  }`}
                >
                  <Image
                    src={image?.url}
                    alt={image?.alt || `${project?.title} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Project Info */}
          <div className="bg-black/30 backdrop-blur-sm rounded-lg p-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="bg-primary text-white px-2 py-1 rounded text-sm font-medium">
                    {project?.serviceTypeLabel}
                  </span>
                  <span className="text-white/80 text-sm">{project?.location}</span>
                </div>
                <p className="text-white/90 text-sm mb-2">{project?.description}</p>
                <div className="flex items-center space-x-4 text-white/70 text-sm">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{project?.duration}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="MapPin" size={14} />
                    <span>{project?.area}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={14} />
                    <span>{project?.completionDate}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <Link
                  to="/service-details"
                  className="inline-flex items-center justify-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  <Icon name="Info" size={16} className="mr-2" />
                  Learn More
                </Link>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleBookService}
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Book Service
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;